import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { ConfidenceBar } from '@/components/px/ConfidenceBar'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { resourcesNav } from '@/components/px/nav'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { personById, type Person } from '@/demo/data'
import {
  BOOKING_TONE,
  DEEP_PROFILE_IDS,
  GROWTH_TONE,
  PERSON_PROFILE_COPY,
  POOL_HREF,
  SKILLS_HREF,
  TRAINING_HREF,
  bookingLabel,
  bookingTotals,
  deepProfileFor,
  isPersonId,
  minimalBooking,
  personChrome,
  personHref,
  phaseName,
  projectsDelivered,
  type Booking,
  type DeepProfile
} from '@/demo/content/personProfile'
import {
  activateUser,
  changeUserRole,
  deactivateUser,
  fetchUser,
  fetchUserActivity
} from '@/features/users/usersSlice'
import {
  selectUserActivity,
  selectUserDetail,
  selectUserDetailCache,
  selectUserDetailError
} from '@/features/users/usersSelectors'
import type { OrgRole } from '@/features/users/types'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { formatDate, formatRelativeTime, initialsFor } from '@/lib/format'
import { useAppDispatch, useAppSelector } from '@/store'
import { isApiError, toApiError } from '@/services'

const copy = PERSON_PROFILE_COPY

const columnHeadClass =
  'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

/** The org roles the admin action may set (owner included — the server gates). */
const ROLE_OPTIONS: readonly OrgRole[] = ['owner', 'admin', 'member', 'viewer']

/* ── Demo growth panels — rendered ONLY for seed person ids, verbatim from the
 * prototype behind the concept badge (a seed fallback for a REAL person would
 * fabricate demo facts; a real id gets the concept placeholder instead). ──── */

function PanelHead({ id, label, note }: { id: string; label: string; note: string }) {
  return (
    <div id={id}>
      <SectionLabel>{label}</SectionLabel>
      <p className="mt-1 text-xs text-muted-foreground">{note}</p>
    </div>
  )
}

function VerifiedSkillsPanel({ person, profile }: { person: Person; profile: DeepProfile }) {
  const { skills = [] } = profile
  return (
    <section
      aria-labelledby="verified-skills-label"
      className="rounded-lg border border-border bg-card px-4 py-3.5"
    >
      <PanelHead id="verified-skills-label" label={copy.skills.label} note={copy.skills.note} />
      <ul className="mt-3" aria-label={copy.skills.listLabel(person.name)}>
        {skills.map((skill) => (
          <li
            key={skill.label}
            className="v-row flex flex-wrap items-center justify-between gap-x-6 gap-y-1 py-2"
          >
            <span className="min-w-0 leading-tight">
              <Link
                to={SKILLS_HREF}
                className="font-medium text-foreground underline-offset-2 hover:underline"
              >
                {skill.label}
              </Link>
              <span className="block text-xs text-muted-foreground">{skill.evidence}</span>
            </span>
            <ConfidenceBar pct={skill.pct} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function PhasesDeliveredPanel({ person, profile }: { person: Person; profile: DeepProfile }) {
  const { phases = [] } = profile
  return (
    <section
      aria-labelledby="phases-label"
      className="rounded-lg border border-border bg-card px-4 py-3.5"
    >
      <PanelHead id="phases-label" label={copy.phases.label} note={copy.phases.note} />
      <Table className="mt-2">
        <TableCaption className="sr-only">{copy.phases.caption(person.name)}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead className={columnHeadClass}>{copy.phases.columns.phase}</TableHead>
            <TableHead className={columnHeadClass}>{copy.phases.columns.projects}</TableHead>
            <TableHead className={columnHeadClass}>{copy.phases.columns.rating}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {phases.map((record) => (
            <TableRow key={record.phase}>
              <TableCell className="font-medium text-foreground">{phaseName(record.phase)}</TableCell>
              <TableCell className="v-figure tabular-nums text-foreground">{record.projects}</TableCell>
              <TableCell className="v-figure tabular-nums text-foreground">
                {copy.phases.ratingLabel(record.rating)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="v-figure mt-2 text-xs tabular-nums text-muted-foreground">
        {copy.phases.totalLine(projectsDelivered(phases), phases.length)}
      </p>
    </section>
  )
}

function GrowthPlanPanel({ profile }: { profile: DeepProfile }) {
  const { growth = [] } = profile
  return (
    <section
      aria-labelledby="growth-label"
      className="rounded-lg border border-border bg-card px-4 py-3.5"
    >
      <PanelHead id="growth-label" label={copy.growth.label} note={copy.growth.note} />
      <ul className="mt-3">
        {growth.map((item) => (
          <li key={item.course} className="v-row py-2.5">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <Link
                to={TRAINING_HREF}
                className="font-medium text-foreground underline-offset-2 hover:underline"
              >
                {item.course}
              </Link>
              <StatusPill tone={GROWTH_TONE[item.status]}>{item.status}</StatusPill>
            </div>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink-soft">
              <span className="text-muted-foreground">{copy.growth.whyLabel}: </span>
              {item.why}
            </p>
          </li>
        ))}
      </ul>
    </section>
  )
}

function BookingRow({ booking }: { booking: Booking }) {
  return (
    <TableRow>
      <TableCell className="max-w-[22rem] whitespace-normal py-2.5">
        <Link
          to={booking.href}
          className="font-medium text-foreground underline-offset-2 hover:underline"
        >
          {bookingLabel(booking)}
        </Link>
        {booking.note !== undefined && (
          <span className="mt-0.5 block text-xs text-muted-foreground">{booking.note}</span>
        )}
      </TableCell>
      <TableCell className="max-w-[20rem] whitespace-normal text-ink-soft">{booking.role}</TableCell>
      <TableCell className="v-figure tabular-nums text-foreground">{booking.weeks}</TableCell>
      <TableCell className="v-figure tabular-nums text-foreground">
        {copy.bookings.hoursLabel(booking.hours)}
      </TableCell>
      <TableCell>
        <StatusPill tone={BOOKING_TONE[booking.status]}>{booking.status}</StatusPill>
      </TableCell>
    </TableRow>
  )
}

function BookingsPanel({ person, profile }: { person: Person; profile: DeepProfile }) {
  const { bookings = [] } = profile
  return (
    <section
      aria-labelledby="bookings-label"
      className="rounded-lg border border-border bg-card px-4 py-3.5"
    >
      <PanelHead id="bookings-label" label={copy.bookings.label} note={copy.bookings.note} />
      <Table className="mt-2">
        <TableCaption className="sr-only">{copy.bookings.caption(person.name)}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead className={columnHeadClass}>{copy.bookings.columns.project}</TableHead>
            <TableHead className={columnHeadClass}>{copy.bookings.columns.role}</TableHead>
            <TableHead className={columnHeadClass}>{copy.bookings.columns.weeks}</TableHead>
            <TableHead className={columnHeadClass}>{copy.bookings.columns.hours}</TableHead>
            <TableHead className={columnHeadClass}>{copy.bookings.columns.status}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.map((booking) => (
            <BookingRow key={`${booking.status}-${booking.role}`} booking={booking} />
          ))}
        </TableBody>
      </Table>
      <p className="v-figure mt-2 text-xs tabular-nums text-muted-foreground">
        {copy.bookings.totalsLine(bookingTotals(bookings))}
      </p>
    </section>
  )
}

function MinimalProfile({ person }: { person: Person }) {
  const { skills = [] } = person
  const booking = minimalBooking(person)
  return (
    <>
      <section
        aria-labelledby="declared-skills-label"
        className="rounded-lg border border-border bg-card px-4 py-3.5"
      >
        <PanelHead
          id="declared-skills-label"
          label={copy.minimal.skillsLabel}
          note={copy.minimal.skillsNote}
        />
        <p className="mt-3 flex flex-wrap gap-1.5">
          {skills.map((skill) => (
            <Badge key={skill} variant="outline" className="font-normal">
              {skill}
            </Badge>
          ))}
        </p>
      </section>
      <section
        aria-labelledby="current-engagement-label"
        className="mt-4 rounded-lg border border-border bg-card px-4 py-3.5"
      >
        <div id="current-engagement-label">
          <SectionLabel>{copy.minimal.bookingLabel}</SectionLabel>
        </div>
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {booking === null ? (
            <StatusPill tone="neutral">{copy.minimal.benchLabel}</StatusPill>
          ) : (
            <>
              <Link
                to={booking.href}
                className="font-medium text-foreground underline-offset-2 hover:underline"
              >
                {booking.label}
              </Link>
              <span className="text-muted-foreground">{booking.role}</span>
            </>
          )}
        </p>
      </section>
      <p className="mt-4 max-w-3xl text-sm leading-relaxed text-ink-soft">
        {copy.minimal.withheldLead}{' '}
        {DEEP_PROFILE_IDS.map((id, index) => (
          <span key={id}>
            {index > 0 && <span aria-hidden> · </span>}
            <Link
              to={personHref(id)}
              className="underline underline-offset-2 hover:text-foreground"
            >
              {personById(id).name}
            </Link>
          </span>
        ))}
        . {copy.minimal.withheldTail}
      </p>
    </>
  )
}

/* ── The page ────────────────────────────────────────────────────────────── */

/**
 * The person profile (mockup 1.2 re-authored, PX-W6.2): the REAL user record
 * (`GET /users/:id`) + that user's recent activity rows + admin actions, with
 * the demo growth panels kept ONLY for seed person ids (a real person gets a
 * concept-badged placeholder, never fabricated demo facts).
 */
export default function PersonProfilePage() {
  const { t } = useTranslation(['people', 'common'])
  const dispatch = useAppDispatch()
  const toMessage = useApiErrorMessage()
  const { personId } = useParams()

  const detail = useAppSelector((s) => selectUserDetail(s))
  const detailError = useAppSelector(selectUserDetailError)
  const detailCache = useAppSelector(selectUserDetailCache)
  const activity = useAppSelector((s) => selectUserActivity(s, personId ?? null))
  const activeOrgId = useAppSelector((s) => s.session.activeOrgId)

  const [roleDraft, setRoleDraft] = useState<OrgRole>('member')
  const [actionError, setActionError] = useState<{ status: number; key?: string } | null>(null)

  const seedPerson = isPersonId(personId) ? personById(personId) : null
  const seedProfile = seedPerson !== null ? deepProfileFor(seedPerson.id) : null
  const chrome = seedPerson !== null ? personChrome(seedPerson) : null

  useEffect(() => {
    if (personId === undefined) return
    void dispatch(fetchUser(personId))
    void dispatch(fetchUserActivity(personId))
  }, [personId, dispatch])

  const orgMembership =
    detail !== undefined
      ? (detail.organisations ?? []).find((org) => org.orgId === activeOrgId)
      : undefined

  const onRoleApplyClicked = async (): Promise<void> => {
    if (detail === undefined || personId === undefined) return
    const confirmed = window.confirm(
      t('people:profile.roleConfirm', { name: detail.name ?? detail.email, role: roleDraft })
    )
    if (!confirmed) return
    setActionError(null)
    try {
      await dispatch(changeUserRole({ userId: personId, role: roleDraft })).unwrap()
    } catch (rejected) {
      setActionError(toActionError(rejected))
    }
  }

  const onToggleActiveClicked = async (): Promise<void> => {
    if (detail === undefined || personId === undefined) return
    const inactive = detail.memberStatus === 'INACTIVE' || detail.deactivated === true
    const confirmed = window.confirm(
      inactive
        ? t('people:profile.activateConfirm', { name: detail.name ?? detail.email })
        : t('people:profile.deactivateConfirm', { name: detail.name ?? detail.email })
    )
    if (!confirmed) return
    setActionError(null)
    try {
      await dispatch(
        (inactive ? activateUser : deactivateUser)(personId)
      ).unwrap()
    } catch (rejected) {
      setActionError(toActionError(rejected))
    }
  }

  return (
    <AppShell
      moduleLabel={chrome?.moduleLabel ?? t('people:chrome.module')}
      breadcrumb={
        <span>
          <Link to={POOL_HREF} className="underline-offset-2 hover:underline">
            {chrome?.breadcrumbPeople ?? t('people:title')}
          </Link>
          <span aria-hidden> › </span>
          {detail?.name ?? chrome?.breadcrumbName ?? personId}
        </span>
      }
      contextPill={chrome?.contextPill ?? t('people:chrome.contextPill')}
      nav={resourcesNav}
    >
      <p className="text-sm">
        <Link to={POOL_HREF} className="text-ink-soft underline-offset-2 hover:underline">
          {chrome?.backLabel ?? t('people:profile.back')}
        </Link>
      </p>

      {personId === undefined ? (
        <div className="mt-4">
          <EmptyPanel />
        </div>
      ) : (
        <>
          {/* ── The real record ─────────────────────────────────────────── */}
          <div className="mt-4">
            {detailCache.loading ? (
              <LoadingPanel />
            ) : detailError !== null && detailError.status === 403 ? (
              <ForbiddenPanel requested={`/{orgId}/users/${personId}`} />
            ) : detailError !== null ? (
              <ErrorPanel error={detailError} onRetryClicked={() => void dispatch(fetchUser(personId))} />
            ) : detail === undefined ? (
              <EmptyPanel title={t('people:empty.title')} body={t('people:empty.body')} />
            ) : (
              <section
                aria-labelledby="person-record-label"
                className="rounded-lg border border-border bg-card px-4 py-4"
              >
                <div id="person-record-label">
                  <SectionLabel>{t('people:profile.recordLabel')}</SectionLabel>
                </div>
                <div className="mt-3 flex flex-wrap items-start gap-x-4 gap-y-3">
                  <span
                    aria-hidden
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-people-bg font-serif text-xl font-semibold text-people-ink"
                  >
                    {initialsFor(detail.name ?? detail.email)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h1 className="font-serif text-2xl font-semibold leading-tight">
                      {detail.name ?? detail.email}
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">{detail.email}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {orgMembership?.role !== undefined && (
                        <StatusPill tone="proposed">{orgMembership.role}</StatusPill>
                      )}
                      <StatusPill tone={detail.memberStatus === 'ACTIVE' ? 'done' : 'neutral'}>
                        {detail.memberStatus !== null && detail.memberStatus !== ''
                          ? t(`people:status.${detail.memberStatus}`, {
                              defaultValue: detail.memberStatus
                            })
                          : t('people:status.unknown')}
                      </StatusPill>
                      {detail.isGuest === true && (
                        <StatusPill tone="neutral">{t('people:profile.guest')}</StatusPill>
                      )}
                    </div>
                  </div>
                </div>

                <dl className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground">{t('people:profile.orgRole')}</dt>
                    <dd className="text-foreground">
                      {orgMembership?.role ?? detail.memberRole ?? t('common:values.unknown')}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground">{t('people:profile.group')}</dt>
                    <dd className="text-foreground">
                      {detail.memberGroup ?? t('common:values.none')}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground">{t('people:profile.joined')}</dt>
                    <dd className="text-foreground">
                      {formatDate(detail.joinedAt) ?? t('common:values.unknown')}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground">{t('people:profile.lastActive')}</dt>
                    <dd className="text-foreground">
                      {formatRelativeTime(detail.lastActiveAt) ?? t('common:values.never')}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground">{t('people:profile.invitedBy')}</dt>
                    <dd className="text-foreground">
                      {detail.invitedBy ?? t('common:values.none')}
                    </dd>
                  </div>
                </dl>
              </section>
            )}
          </div>

          {/* ── Administration (per-person acts; the server gates them) ──── */}
          {detail !== undefined && (
            <section
              aria-labelledby="person-admin-label"
              className="mt-4 rounded-lg border border-border bg-card px-4 py-4"
            >
              <div id="person-admin-label">
                <SectionLabel>{t('people:profile.adminLabel')}</SectionLabel>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-1.5 text-sm text-ink-soft">
                  {t('people:profile.roleAction')}
                  <select
                    value={roleDraft}
                    onChange={(event) => setRoleDraft(event.target.value as OrgRole)}
                    className="rounded-md border border-input bg-card px-2 py-1 text-sm text-foreground"
                  >
                    {ROLE_OPTIONS.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => void onRoleApplyClicked()}
                  className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  {t('people:profile.roleApply')}
                </button>
                <button
                  type="button"
                  onClick={() => void onToggleActiveClicked()}
                  className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted"
                >
                  {detail.memberStatus === 'INACTIVE' || detail.deactivated === true
                    ? t('people:profile.activate')
                    : t('people:profile.deactivate')}
                </button>
              </div>
              {actionError !== null &&
                (actionError.status === 403 ? (
                  <div className="mt-3">
                    <ForbiddenPanel requested={`/{orgId}/users/${personId}/role`} />
                  </div>
                ) : (
                  <p role="alert" className="mt-2 text-sm text-blocked">
                    {toMessage({ status: actionError.status, backendKey: actionError.key, message: '' }) ??
                      t('common:states.errorTitle')}
                  </p>
                ))}
            </section>
          )}

          {/* ── Recent activity (server rows) ────────────────────────────── */}
          <section
            aria-labelledby="person-activity-label"
            className="mt-4 rounded-lg border border-border bg-card px-4 py-4"
          >
            <div id="person-activity-label">
              <SectionLabel>{t('people:profile.activityLabel')}</SectionLabel>
            </div>
            {activity.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {t('people:profile.activityEmpty')}
              </p>
            ) : (
              <ul className="mt-2 divide-y divide-border">
                {activity.map((entry, index) => (
                  <li key={entry.id ?? index} className="flex flex-wrap gap-x-4 gap-y-0.5 py-2">
                    <span className="min-w-0 flex-1 text-sm text-foreground">
                      {entry.content ?? entry.type ?? '—'}
                    </span>
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {formatRelativeTime(entry.createdAtMillis ?? entry.createdAt) ?? ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <Separator className="mt-5" />

          {/* ── Growth: demo panels for seed ids, concept placeholder else ── */}
          <section aria-labelledby="person-growth-label" className="mt-4">
            <div id="person-growth-label" className="flex items-center gap-2">
              <SectionLabel>{t('people:profile.growthLabel')}</SectionLabel>
              <ConceptBadge />
            </div>
            <div className="mt-3">
              {seedPerson !== null && seedProfile !== null ? (
                <>
                  <div className="grid gap-4 xl:grid-cols-2">
                    <VerifiedSkillsPanel person={seedPerson} profile={seedProfile} />
                    <PhasesDeliveredPanel person={seedPerson} profile={seedProfile} />
                  </div>
                  <div className="mt-4">
                    <GrowthPlanPanel profile={seedProfile} />
                  </div>
                  <div className="mt-4">
                    <BookingsPanel person={seedPerson} profile={seedProfile} />
                  </div>
                </>
              ) : seedPerson !== null ? (
                <MinimalProfile person={seedPerson} />
              ) : (
                <EmptyPanel
                  title={t('people:profile.growthConceptTitle')}
                  body={t('people:profile.growthConceptBody')}
                />
              )}
            </div>
          </section>
        </>
      )}
    </AppShell>
  )
}

/** Narrow a thunk rejection into the shape the admin-error branch renders. */
function toActionError(rejected: unknown): { status: number; key?: string } {
  if (isApiError(rejected)) return { status: rejected.status, key: rejected.backendKey }
  const mapped = toApiError(rejected)
  return { status: mapped.status, key: mapped.backendKey }
}
